import { useState, useEffect } from "react";
import { useAuth } from '../../auth/AuthProvider';
import { useTranslation } from 'react-i18next';
import { Trash2, Edit, PlusCircle, Calendar, X, Check, AlertCircle, ChevronDown, ChevronUp, Loader } from "lucide-react";
import { format, parseISO } from 'date-fns';

export default function AcademicYearsManagement() {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation('academicyearsmanagement');
  const isRTL = i18n.language === 'ar';

  const [academicYears, setAcademicYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedYear, setExpandedYear] = useState(null);

  // State for modals
  const [showDeleteYearModal, setShowDeleteYearModal] = useState(false);
  const [showEditYearModal, setShowEditYearModal] = useState(false);
  const [showAddYearModal, setShowAddYearModal] = useState(false);
  const [showDeleteTermModal, setShowDeleteTermModal] = useState(false);
  const [showEditTermModal, setShowEditTermModal] = useState(false);
  const [showAddTermModal, setShowAddTermModal] = useState(false);

  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);

  // Form states
  const [yearFormData, setYearFormData] = useState({
    year_name: "",
    start_date: "",
    end_date: "",
    is_current_year: false,
    school_id: user?.school_id
  });

  const [termFormData, setTermFormData] = useState({
    term_name: "",
    start_date: "",
    end_date: "",
    academic_year_id: null,
    school_id: user?.school_id
  });

  // Notification state
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });

  // Fetch academic years and terms on component mount
  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
  }, []);

  const fetchAcademicYears = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/v1/academic-years');
      setAcademicYears(response.data);
    } catch (err) {
      setError(t('errors.fetchAcademicYears'));
      showNotification(t('errors.fetchAcademicYears'), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await api.get('/api/v1/terms');
      setTerms(response.data);
    } catch (err) {
      showNotification(t('errors.fetchTerms'), "error");
    }
  };

  const handleDeleteYear = async () => {
    try {
      // Use 'id' based on your API structure
      await api.delete(`/api/v1/academic-years/${selectedYear.id}`);
      setAcademicYears(academicYears.filter(year => year.id !== selectedYear.id));
      // Also remove terms associated with this year
      setTerms(terms.filter(term => term.academic_year_id !== selectedYear.id));
      showNotification(t('success.yearDeleted', { yearName: selectedYear.year_name }), "success");
    } catch (err) {
      handleApiError(err, t('errors.deleteYear'));
    } finally {
      setShowDeleteYearModal(false);
    }
  };

  const handleEditYear = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/api/v1/academic-years/${selectedYear.id}`, yearFormData);

      // Update the academic year in the list
      setAcademicYears(academicYears.map(year =>
        year.id === selectedYear.id ? response.data : year
      ));

      showNotification(t('success.yearUpdated', { yearName: yearFormData.year_name }), "success");
      setShowEditYearModal(false);
    } catch (err) {
      handleApiError(err, t('errors.updateYear'));
    }
  };

  const handleAddYear = async (e) => {
    e.preventDefault();
    try {
      const payloadWithSchoolId = {
        ...yearFormData,
        school_id: user?.school_id
      };

      const response = await api.post('/api/v1/academic-years', payloadWithSchoolId);
      setAcademicYears([...academicYears, response.data]);
      showNotification(t('success.yearCreated', { yearName: yearFormData.year_name }), "success");
      setShowAddYearModal(false);
      resetYearForm();
    } catch (err) {
      handleApiError(err, t('errors.createYear'));
    }
  };

  const handleDeleteTerm = async () => {
    try {
      await api.delete(`/api/v1/terms/${selectedTerm.term_id}`);
      setTerms(terms.filter(term => term.term_id !== selectedTerm.term_id));
      showNotification(t('success.termDeleted', { termName: selectedTerm.term_name }), "success");
    } catch (err) {
      handleApiError(err, t('errors.deleteTerm'));
    } finally {
      setShowDeleteTermModal(false);
    }
  };

  const handleEditTerm = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/api/v1/terms/${selectedTerm.term_id}`, termFormData);

      // Update the term in the list
      setTerms(terms.map(term =>
        term.term_id === selectedTerm.term_id ? response.data : term
      ));

      showNotification(t('success.termUpdated', { termName: termFormData.term_name }), "success");
      setShowEditTermModal(false);
    } catch (err) {
      handleApiError(err, t('errors.updateTerm'));
    }
  };

  const handleAddTerm = async (e) => {
    e.preventDefault();
    try {
      const payloadWithSchoolId = {
        ...termFormData,
        school_id: user?.school_id,
        academic_year_id: selectedYear.id
      };

      const response = await api.post('/api/v1/terms', payloadWithSchoolId);
      setTerms([...terms, response.data]);
      showNotification(t('success.termCreated', { termName: termFormData.term_name }), "success");
      setShowAddTermModal(false);
      resetTermForm();
    } catch (err) {
      handleApiError(err, t('errors.createTerm'));
    }
  };

  const openEditYearModal = (year) => {
    setSelectedYear(year);
    setYearFormData({
      year_name: year.year_name,
      start_date: year.start_date,
      end_date: year.end_date,
      is_current_year: year.is_current_year,
      school_id: user?.school_id
    });
    setShowEditYearModal(true);
  };

  const openAddYearModal = () => {
    resetYearForm();
    setShowAddYearModal(true);
  };

  const openEditTermModal = (term) => {
    setSelectedTerm(term);
    setTermFormData({
      term_name: term.term_name,
      start_date: term.start_date,
      end_date: term.end_date,
      academic_year_id: term.academic_year_id,
      school_id: user?.school_id
    });
    setShowEditTermModal(true);
  };

  const openAddTermModal = (year) => {
    setSelectedYear(year);
    resetTermForm();
    setTermFormData(prev => ({
      ...prev,
      academic_year_id: year.id,
      // Set default dates within the academic year range
      start_date: year.start_date,
      end_date: year.end_date
    }));
    setShowAddTermModal(true);
  };

  const resetYearForm = () => {
    setYearFormData({
      year_name: "",
      start_date: "",
      end_date: "",
      is_current_year: false,
      school_id: user?.school_id
    });
  };

  const resetTermForm = () => {
    setTermFormData({
      term_name: "",
      start_date: "",
      end_date: "",
      academic_year_id: null,
      school_id: user?.school_id
    });
  };

  const handleYearInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setYearFormData({
      ...yearFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleTermInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTermFormData({
      ...termFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const showNotification = (message, type = "info") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 5000);
  };

  const handleApiError = (err, defaultMessage) => {
    if (err.response && err.response.data) {
      if (typeof err.response.data.detail === 'string') {
        showNotification(err.response.data.detail, "error");
        return;
      }

      if (Array.isArray(err.response.data.detail) && err.response.data.detail.length > 0) {
        showNotification(err.response.data.detail[0].msg, "error");
        return;
      }

      if (err.response.data.detail) {
        showNotification(JSON.stringify(err.response.data.detail), "error");
        return;
      }
    }

    // Default fallback message
    showNotification(defaultMessage, "error");
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const toggleYearExpansion = (yearId) => {
    if (expandedYear === yearId) {
      setExpandedYear(null);
    } else {
      setExpandedYear(yearId);
    }
  };

  const getTermsForYear = (yearId) => {
    return terms.filter(term => term.academic_year_id === yearId);
  };

  return (
    <div className={`max-w-7xl mx-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white border border-gray-100 overflow-hidden">
        {/* Header with Title and Add Button */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{t('title')}</h1>
            <button
              onClick={openAddYearModal}
              className={`text-white bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-lg flex items-center transition-all duration-200 text-sm ${isRTL ? 'flex-row-reverse' : ''
                }`}
            >
              <PlusCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('addAcademicYear')}
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification.show && (
          <div className="mx-6 sm:mx-8 mb-4" key="notification">
            <div
              className={`p-4 rounded-lg flex items-center justify-between ${notification.type === 'error' ? 'bg-red-50 text-red-600 border border-red-100' :
                  notification.type === 'success' ? 'bg-green-50 text-green-600 border border-green-100' :
                    'bg-blue-50 text-blue-600 border border-blue-100'
                }`}
            >
              <div className="flex items-center">
                {notification.type === 'error' ? <AlertCircle className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} /> :
                  notification.type === 'success' ? <Check className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} /> :
                    <Calendar className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />}
                <span>{notification.message}</span>
              </div>
              <button
                onClick={() => setNotification({ show: false, message: "", type: "" })}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <div className="p-4">
          {/* Loading State */}
          {isLoading ? (
            <div className="flex items-center justify-center p-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-4 rounded-lg text-red-600 border border-red-100">
              <div className="flex items-center">
                <AlertCircle className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <span>{error}</span>
              </div>
            </div>
          ) : academicYears.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-100">
              <Calendar className="h-16 w-16 mx-auto text-gray-400 mb-3" />
              <h3 className="text-lg font-medium text-gray-700">{t('noAcademicYears')}</h3>
              <p className="text-gray-500 mt-2">{t('createFirstYear')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {academicYears.map((year) => (
                <div key={year.id} className="border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                  {/* Year header */}
                  <div
                    className={`flex justify-between items-center p-5 cursor-pointer ${year.is_current_year ? 'bg-teal-50' : 'bg-gray-50'
                      }`}
                    onClick={() => toggleYearExpansion(year.id)}
                  >
                    <div className="flex items-center">
                      {expandedYear === year.id ? (
                        <ChevronUp className={`h-5 w-5 text-teal-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                      ) : (
                        <ChevronDown className={`h-5 w-5 text-teal-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                      )}
                      <div className={isRTL ? 'text-right' : 'text-left'}>
                        <h3 className="font-medium text-gray-800">{year.year_name}</h3>
                        <p className="text-sm text-gray-500">
                          {formatDate(year.start_date)} - {formatDate(year.end_date)}
                        </p>
                      </div>
                      {year.is_current_year && (
                        <span className={`${isRTL ? 'mr-3' : 'ml-3'} px-2 py-1 text-xs font-medium rounded-full bg-green-50 text-green-600 border border-green-200`}>
                          {t('current')}
                        </span>
                      )}
                    </div>

                    <div className={`flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditYearModal(year);
                        }}
                        className="text-gray-400 hover:text-teal-600 p-1 rounded-full hover:bg-teal-50"
                        title={t('editAcademicYear')}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedYear(year);
                          setShowDeleteYearModal(true);
                        }}
                        className="text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50"
                        title={t('deleteAcademicYear')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Terms for this year */}
                  {expandedYear === year.id && (
                    <div className="p-5 bg-white border-t border-gray-100">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-medium text-gray-700">{t('terms')}</h4>
                        <button
                          onClick={() => openAddTermModal(year)}
                          className={`text-teal-600 hover:bg-teal-50 px-3 py-1.5 rounded-lg flex items-center transition-all duration-200 text-sm border border-teal-200 ${isRTL ? 'flex-row-reverse' : ''
                            }`}
                        >
                          <PlusCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          {t('addTerm')}
                        </button>
                      </div>

                      {getTermsForYear(year.id).length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
                          <p className="text-gray-500">{t('noTermsFound')}</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white rounded-lg overflow-hidden">
                            <thead className="bg-gray-50 text-left">
                              <tr>
                                <th className={`py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'
                                  }`}>
                                  {t('name')}
                                </th>
                                <th className={`py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'
                                  }`}>
                                  {t('period')}
                                </th>
                                <th className={`py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${isRTL ? 'text-left' : 'text-right'
                                  }`}>
                                  {t('actions')}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {getTermsForYear(year.id).map((term) => (
                                <tr key={term.term_id} className="hover:bg-gray-50">
                                  <td className={`py-3 px-4 text-sm font-medium text-gray-800 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'
                                    }`}>
                                    {term.term_name}
                                  </td>
                                  <td className={`py-3 px-4 text-sm text-gray-600 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'
                                    }`}>
                                    {formatDate(term.start_date)} - {formatDate(term.end_date)}
                                  </td>
                                  <td className={`py-3 px-4 whitespace-nowrap ${isRTL ? 'text-left' : 'text-right'
                                    }`}>
                                    <div className={`flex ${isRTL ? 'justify-end space-x-reverse space-x-2' : 'justify-end space-x-2'}`}>
                                      <button
                                        onClick={() => openEditTermModal(term)}
                                        className="text-gray-400 hover:text-teal-600 p-1 rounded-full hover:bg-teal-50"
                                        title={t('editTerm')}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          setSelectedTerm(term);
                                          setShowDeleteTermModal(true);
                                        }}
                                        className="text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50"
                                        title={t('deleteTerm')}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Year Confirmation Modal */}
      {showDeleteYearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-800">{t('modals.deleteYear.title')}</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                {t('modals.deleteYear.message', { yearName: selectedYear?.year_name })}
              </p>
              <div className={`flex ${isRTL ? 'justify-start space-x-reverse space-x-4' : 'justify-end space-x-4'}`}>
                <button
                  onClick={() => setShowDeleteYearModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleDeleteYear}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Term Confirmation Modal */}
      {showDeleteTermModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-800">{t('modals.deleteTerm.title')}</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                {t('modals.deleteTerm.message', { termName: selectedTerm?.term_name })}
              </p>
              <div className={`flex ${isRTL ? 'justify-start space-x-reverse space-x-4' : 'justify-end space-x-4'}`}>
                <button
                  onClick={() => setShowDeleteTermModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleDeleteTerm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Add Academic Year Modal */}
      {(showEditYearModal || showAddYearModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                {showEditYearModal ? t('modals.editYear.title') : t('modals.addYear.title')}
              </h2>
              <button
                onClick={() => {
                  showEditYearModal ? setShowEditYearModal(false) : setShowAddYearModal(false);
                }}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={showEditYearModal ? handleEditYear : handleAddYear} className="p-6">
              <div className="space-y-6">
                <div key="year-name-field">
                  <label htmlFor="year_name" className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('forms.yearName')}
                  </label>
                  <input
                    type="text"
                    id="year_name"
                    name="year_name"
                    value={yearFormData.year_name}
                    onChange={handleYearInputChange}
                    required
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'
                      }`}
                    placeholder={t('forms.yearNamePlaceholder')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                <div key="year-start-date-field">
                  <label htmlFor="start_date" className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('forms.startDate')}
                  </label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={yearFormData.start_date}
                    onChange={handleYearInputChange}
                    required
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'
                      }`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                <div key="year-end-date-field">
                  <label htmlFor="end_date" className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('forms.endDate')}
                  </label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={yearFormData.end_date}
                    onChange={handleYearInputChange}
                    required
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'
                      }`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                <div key="year-current-checkbox" className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <input
                    type="checkbox"
                    id="is_current_year"
                    name="is_current_year"
                    checked={yearFormData.is_current_year}
                    onChange={handleYearInputChange}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_current_year" className={`block text-sm text-gray-700 ${isRTL ? 'mr-2' : 'ml-2'}`}>
                    {t('forms.setCurrentYear')}
                  </label>
                </div>
              </div>

              <div className={`mt-8 flex ${isRTL ? 'justify-start space-x-reverse space-x-4' : 'justify-end space-x-4'}`}>
                <button
                  type="button"
                  onClick={() => {
                    showEditYearModal ? setShowEditYearModal(false) : setShowAddYearModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {showEditYearModal ? t('update') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit/Add Term Modal */}
      {(showEditTermModal || showAddTermModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                {showEditTermModal ? t('modals.editTerm.title') : t('modals.addTerm.title')}
              </h2>
              <button
                onClick={() => {
                  showEditTermModal ? setShowEditTermModal(false) : setShowAddTermModal(false);
                }}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={showEditTermModal ? handleEditTerm : handleAddTerm} className="p-6">
              <div className="space-y-6">
                <div key="term-name-field">
                  <label htmlFor="term_name" className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('forms.termName')}
                  </label>
                  <input
                    type="text"
                    id="term_name"
                    name="term_name"
                    value={termFormData.term_name}
                    onChange={handleTermInputChange}
                    required
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'
                      }`}
                    placeholder={t('forms.termNamePlaceholder')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                <div key="term-start-date-field">
                  <label htmlFor="term_start_date" className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('forms.startDate')}
                  </label>
                  <input
                    type="date"
                    id="term_start_date"
                    name="start_date"
                    value={termFormData.start_date}
                    onChange={handleTermInputChange}
                    required
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'
                      }`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                <div key="term-end-date-field">
                  <label htmlFor="term_end_date" className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('forms.endDate')}
                  </label>
                  <input
                    type="date"
                    id="term_end_date"
                    name="end_date"
                    value={termFormData.end_date}
                    onChange={handleTermInputChange}
                    required
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'
                      }`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                {showAddTermModal && (
                  <div key="term-academic-year-info" className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <p className="text-blue-700">
                      {t('forms.addingTermTo')}: <span className="font-semibold">{selectedYear?.year_name}</span>
                    </p>
                    <p className="text-blue-600 text-xs mt-1">
                      {t('forms.academicYearPeriod')}: {formatDate(selectedYear?.start_date)} - {formatDate(selectedYear?.end_date)}
                    </p>
                  </div>
                )}
              </div>

              <div className={`mt-8 flex ${isRTL ? 'justify-start space-x-reverse space-x-4' : 'justify-end space-x-4'}`}>
                <button
                  type="button"
                  onClick={() => {
                    showEditTermModal ? setShowEditTermModal(false) : setShowAddTermModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {showEditTermModal ? t('update') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}